import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Archive, DollarSign, TrendingUp, Hash, Trash2, Edit, ChevronDown, ChevronUp, ShoppingCart, Camera, LayoutGrid, List, Download, Upload, Sparkles, Layers } from "lucide-react";
import { toast } from "sonner";
import PurchaseFormFields from "../components/PurchaseFormFields";
import AddCustomItemDialog from "../components/AddCustomItemDialog";
import ValuationDialog from "../components/ValuationDialog";

function PurchaseRow({ entry, onEdit, onDelete }) {
  const qty = entry.quantity || entry.quantity_owned || 1;
  const photoCount = (entry.photos || []).length;
  return (
    <div data-testid={`purchase-row-${entry.entry_id}`} className="flex items-center gap-3 py-2 px-3 bg-zinc-800/30 rounded-lg text-xs">
      {/* Photo thumbnails */}
      {photoCount > 0 && (
        <div className="flex gap-1 flex-shrink-0">
          {(entry.photos || []).slice(0, 3).map((p) => (
            <img key={p.photo_id} src={resolveImageUrl(p.url)} alt="" className="w-8 h-8 rounded object-cover border border-zinc-700/30" />
          ))}
          {photoCount > 3 && <span className="w-8 h-8 rounded bg-zinc-700/50 flex items-center justify-center text-[10px] text-zinc-400">+{photoCount - 3}</span>}
        </div>
      )}
      <div className="flex-1 flex items-center gap-4 flex-wrap">
        {entry.purchase_price != null && <span className="text-zinc-300">Paid: <span className="font-mono text-emerald-400">${entry.purchase_price}</span></span>}
        <span className="text-zinc-500">Qty: {qty}</span>
        <span className="text-zinc-500">{entry.condition}</span>
        {entry.seller && <span className="text-zinc-600">From: {entry.seller}</span>}
        {entry.purchase_date && <span className="text-zinc-600">{entry.purchase_date}</span>}
        {entry.storage_location && <span className="text-zinc-600">@ {entry.storage_location}</span>}
        {photoCount > 0 && <span className="text-zinc-500 flex items-center gap-0.5"><Camera className="w-3 h-3" />{photoCount}</span>}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button data-testid={`edit-entry-${entry.entry_id}`} onClick={() => onEdit(entry)} className="p-1 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 rounded transition-colors"><Edit className="w-3.5 h-3.5" /></button>
        <button data-testid={`remove-entry-${entry.entry_id}`} onClick={() => onDelete(entry.entry_id)} className="p-1 text-zinc-500 hover:text-rose-400 hover:bg-zinc-700 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function CollectionGroupCard({ release: r, entries, isExpanded, onToggle, onEdit, onRemove, onNavigate }) {
  const releaseId = r.release_id || entries[0]?.release_id;
  const hasMultiple = entries.length > 1;
  const totalQty = entries.reduce((s, e) => s + (e.quantity || e.quantity_owned || 1), 0);
  const totalSpent = entries.reduce((s, e) => s + ((e.purchase_price || 0) * (e.quantity || e.quantity_owned || 1)), 0);
  const firstUserPhoto = entries.flatMap((e) => e.photos || [])[0];
  const thumbSrc = firstUserPhoto ? resolveImageUrl(firstUserPhoto.url) : (r.model_image ? resolveImageUrl(r.model_image) : null);
  const isUserPhoto = Boolean(firstUserPhoto);

  return (
    <div data-testid={`collection-group-${releaseId}`} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors">
      <div className="p-4 flex gap-4 items-center">
        {thumbSrc ? (
          <div className="relative flex-shrink-0">
            <img src={thumbSrc} alt={r.release_name} className="w-20 h-20 rounded-lg object-cover cursor-pointer" onClick={() => onNavigate(releaseId)} />
            {isUserPhoto && (
              <span data-testid={`user-photo-badge-${releaseId}`} className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-1 shadow-lg" title="Your photo">
                <Camera className="w-2.5 h-2.5" />
              </span>
            )}
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg bg-zinc-800 flex flex-col items-center justify-center cursor-pointer flex-shrink-0" onClick={() => onNavigate(releaseId)}>
            <span className="text-[9px] text-zinc-600 font-mono">{r.khmg_number}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-zinc-100 truncate cursor-pointer hover:text-rose-400" onClick={() => onNavigate(releaseId)}>{r.release_name}</p>
            {r.source === "user_custom" && r.brand && (
              <span className="bg-rose-600/15 text-rose-400 border border-rose-500/25 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">{r.brand}</span>
            )}
            {r.is_chase && <span className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full text-[10px] font-medium">CHASE</span>}
            {r.is_blkltd && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded-full text-[10px] font-medium">BLKLTD</span>}
            {hasMultiple && <span className="bg-sky-500/10 text-sky-400 border border-sky-500/20 px-2 py-0.5 rounded-full text-[10px] font-medium">{entries.length} purchases</span>}
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">{r.vehicle} &middot; {r.series}</p>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {totalSpent > 0 && <span className="text-xs text-zinc-400">Spent: <span className="font-mono text-zinc-300">${totalSpent.toFixed(2)}</span></span>}
            <span className="text-xs text-zinc-400">Value: <span className="font-mono text-emerald-400">{r.estimated_market_value != null ? `$${(r.estimated_market_value * totalQty).toFixed(2)}` : 'TBD'}</span></span>
            <span className="text-xs text-zinc-500">Qty: {totalQty}</span>
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0 items-center">
          {hasMultiple ? (
            <button data-testid={`expand-${releaseId}`} onClick={() => onToggle(releaseId)} className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors">
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          ) : (
            <>
              <button data-testid={`edit-entry-${entries[0].entry_id}`} onClick={() => onEdit(entries[0])} className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors"><Edit className="w-4 h-4" /></button>
              <button data-testid={`remove-entry-${entries[0].entry_id}`} onClick={() => onRemove(entries[0].entry_id)} className="p-2 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>
      {hasMultiple && isExpanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-zinc-800/50 pt-3">
          {entries.map((entry) => <PurchaseRow key={entry.entry_id} entry={entry} onEdit={onEdit} onDelete={onRemove} />)}
        </div>
      )}
    </div>
  );
}

function CollectionGridCard({ release: r, entries, onNavigate }) {
  const releaseId = r.release_id || entries[0]?.release_id;
  const totalQty = entries.reduce((s, e) => s + (e.quantity || e.quantity_owned || 1), 0);
  const value = r.estimated_market_value != null ? r.estimated_market_value * totalQty : null;
  const allUserPhotos = entries.flatMap((e) => e.photos || []);
  const firstUserPhoto = allUserPhotos[0];
  const imageSrc = firstUserPhoto ? resolveImageUrl(firstUserPhoto.url) : (r.model_image ? resolveImageUrl(r.model_image) : null);
  const isUserPhoto = Boolean(firstUserPhoto);

  return (
    <button
      data-testid={`collection-grid-${releaseId}`}
      onClick={() => onNavigate(releaseId)}
      className="group relative bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl overflow-hidden hover:border-rose-500/40 hover:-translate-y-0.5 transition-all text-left"
    >
      <div className="aspect-square bg-zinc-950 relative overflow-hidden">
        {imageSrc ? (
          <img src={imageSrc} alt={r.release_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
            <Archive className="w-8 h-8 text-zinc-800" />
            <span className="text-[9px] text-zinc-700 font-mono">{r.khmg_number}</span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {r.source === "user_custom" && r.brand && (
            <span className="bg-rose-600/90 text-white px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide uppercase">{r.brand}</span>
          )}
          {r.is_chase && <span className="bg-amber-500/90 text-black px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide">CHASE</span>}
          {r.is_blkltd && <span className="bg-purple-500/90 text-white px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide">BLKLTD</span>}
        </div>
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          {totalQty > 1 && (
            <div className="bg-zinc-950/80 backdrop-blur border border-white/10 text-zinc-200 px-2 py-0.5 rounded-full text-[10px] font-mono">
              ×{totalQty}
            </div>
          )}
          {isUserPhoto && (
            <div data-testid={`grid-user-photo-badge-${releaseId}`} className="bg-rose-500/90 text-white backdrop-blur rounded-full p-1 shadow-lg flex items-center gap-1" title="Your photo">
              <Camera className="w-3 h-3" />
              {allUserPhotos.length > 1 && <span className="text-[9px] font-bold pr-1">{allUserPhotos.length}</span>}
            </div>
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent">
          {value != null && (
            <p className="font-mono text-sm font-bold text-emerald-400">${value.toFixed(2)}</p>
          )}
        </div>
      </div>
      <div className="p-3">
        <p className="text-xs font-medium text-zinc-100 truncate">{r.release_name || 'Unknown'}</p>
        <p className="text-[10px] text-zinc-500 truncate mt-0.5">{r.vehicle}</p>
      </div>
    </button>
  );
}

function SourceTab({ active, onClick, icon: Icon, label, count, testId, accent = "text-zinc-300" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active
          ? `bg-zinc-800 ${accent}`
          : "text-zinc-500 hover:text-zinc-300"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
        active ? "bg-zinc-950/60 text-zinc-200" : "bg-zinc-800/60 text-zinc-500 group-hover:text-zinc-400"
      }`}>{count}</span>
    </button>
  );
}

export default function CollectionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedReleases, setExpandedReleases] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('collection_view_mode') || 'list'; } catch { return 'list'; }
  });
  // Source tab: "all" (default) | "catalog" (non-user_custom) | "personal"
  // (user's own custom items). Persisted across reloads so collectors who
  // primarily curate their own list don't have to re-toggle every visit.
  const [sourceTab, setSourceTab] = useState(() => {
    try { return localStorage.getItem('collection_source_tab') || 'all'; } catch { return 'all'; }
  });
  const navigate = useNavigate();

  const changeView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('collection_view_mode', mode); } catch { /* ignore */ }
  };

  const changeSourceTab = (tab) => {
    setSourceTab(tab);
    try { localStorage.setItem('collection_source_tab', tab); } catch { /* ignore */ }
  };

  const fetchCollection = useCallback(async () => {
    try {
      const { data } = await api.get("/collection");
      setData(data);
    } catch { toast.error("Failed to load collection"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCollection(); }, [fetchCollection]);

  const grouped = useMemo(() => {
    if (!data?.entries) return [];
    const map = {};
    for (const entry of data.entries) {
      const rid = entry.release_id;
      if (!map[rid]) map[rid] = { release: entry.release || {}, entries: [] };
      map[rid].entries.push(entry);
    }
    return Object.values(map);
  }, [data]);

  // Split groups by source so we can show counts AND filter without
  // re-running expensive useMemo dependencies for each tab change.
  const personalGroups = useMemo(
    () => grouped.filter((g) => g.release?.source === "user_custom"),
    [grouped]
  );
  const catalogGroups = useMemo(
    () => grouped.filter((g) => g.release?.source !== "user_custom"),
    [grouped]
  );
  const visibleGroups = sourceTab === "personal"
    ? personalGroups
    : sourceTab === "catalog"
      ? catalogGroups
      : grouped;

  const handleRemove = async (entryId) => {
    try {
      await api.delete(`/collection/${entryId}`);
      toast.success("Removed from collection");
      fetchCollection();
    } catch { toast.error("Failed to remove"); }
  };

  const startEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      purchase_price: entry.purchase_price ?? "",
      seller: entry.seller || "",
      condition: entry.condition || "Mint",
      packaging_condition: entry.packaging_condition || "Mint",
      storage_location: entry.storage_location || "",
      notes: entry.notes || "",
      quantity: entry.quantity || entry.quantity_owned || 1,
    });
  };

  const handleUpdate = async () => {
    if (!editEntry) return;
    try {
      const payload = {};
      if (editForm.purchase_price !== undefined) payload.purchase_price = editForm.purchase_price ? parseFloat(editForm.purchase_price) : null;
      if (editForm.seller !== undefined) payload.seller = editForm.seller || null;
      if (editForm.condition) payload.condition = editForm.condition;
      if (editForm.packaging_condition) payload.packaging_condition = editForm.packaging_condition;
      if (editForm.storage_location !== undefined) payload.storage_location = editForm.storage_location || null;
      if (editForm.notes !== undefined) payload.notes = editForm.notes || null;
      if (editForm.quantity) payload.quantity = parseInt(editForm.quantity);
      await api.put(`/collection/${editEntry.entry_id}`, payload);
      toast.success("Updated");
      setEditEntry(null);
      fetchCollection();
    } catch { toast.error("Failed to update"); }
  };

  const toggleExpand = (releaseId) => {
    setExpandedReleases((prev) => ({ ...prev, [releaseId]: !prev[releaseId] }));
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {["coll-skel-1", "coll-skel-2", "coll-skel-3", "coll-skel-4"].map((id) => <div key={id} className="h-24 bg-zinc-900 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const metricCards = [
    { label: "Collection Value", value: `$${metrics.total_value?.toLocaleString() || 0}`, icon: DollarSign, color: "text-emerald-400" },
    { label: "Total Spent", value: `$${metrics.total_spent?.toLocaleString() || 0}`, icon: TrendingUp, color: "text-zinc-400" },
    { label: "Unique Releases", value: metrics.unique_releases || 0, icon: Archive, color: "text-blue-400" },
    { label: "Purchases", value: metrics.total_purchases || 0, icon: ShoppingCart, color: "text-amber-400" },
    { label: "Total Quantity", value: metrics.total_quantity || 0, icon: Hash, color: "text-sky-400" },
    { label: "Est. Profit", value: `$${metrics.estimated_profit?.toLocaleString() || 0}`, icon: TrendingUp, color: metrics.estimated_profit >= 0 ? "text-emerald-400" : "text-rose-400" },
  ];

  return (
    <div data-testid="collection-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight">My Collection</h1>
          <p className="text-zinc-500 mt-1 text-sm">{metrics.unique_releases || 0} unique models &middot; {metrics.total_purchases || 0} purchases</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ValuationDialog />
          <Button
            data-testid="bulk-import-cta-btn"
            variant="outline"
            size="sm"
            onClick={() => navigate("/import")}
            className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-amber-500/40 hover:text-amber-400"
          >
            <Upload className="w-3.5 h-3.5 mr-1" /> Bulk import
          </Button>
          <Button
            data-testid="shelfscan-cta-btn"
            variant="outline"
            size="sm"
            onClick={() => navigate("/shelf-scan")}
            className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-amber-500/40 hover:text-amber-400"
          >
            <Camera className="w-3.5 h-3.5 mr-1" /> Shelf-scan
          </Button>
          <Button
            data-testid="export-csv-btn"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await api.get("/collection/export.csv", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `castfolio-collection-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("CSV downloaded");
              } catch {
                toast.error("Export failed");
              }
            }}
            className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-400"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
          <AddCustomItemDialog onCreated={fetchCollection} />
          <div data-testid="view-toggle" className="inline-flex bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-lg p-1">
            <button
              data-testid="view-toggle-list"
              onClick={() => changeView('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              <List className="w-3.5 h-3.5" />List
            </button>
            <button
              data-testid="view-toggle-grid"
              onClick={() => changeView('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />Grid
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {metricCards.map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.label} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
              <Icon className={`w-4 h-4 ${m.color} mb-1`} />
              <p className={`font-mono text-lg font-bold ${m.color}`}>{m.value}</p>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{m.label}</p>
            </div>
          );
        })}
      </div>

      {/* Source tabs — split a collector's list into "All", official catalog
          releases, and their own custom adds. The personal tab is the
          discovery surface for one-off items added via Quick Add / Scan
          handoff that won't ever appear in the official catalog. */}
      {grouped.length > 0 && (
        <div data-testid="collection-source-tabs" className="flex items-center gap-1 bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-1 w-fit">
          <SourceTab
            active={sourceTab === "all"}
            onClick={() => changeSourceTab("all")}
            icon={Layers}
            label="All"
            count={grouped.length}
            testId="collection-tab-all"
          />
          <SourceTab
            active={sourceTab === "catalog"}
            onClick={() => changeSourceTab("catalog")}
            icon={Archive}
            label="Catalog"
            count={catalogGroups.length}
            testId="collection-tab-catalog"
          />
          <SourceTab
            active={sourceTab === "personal"}
            onClick={() => changeSourceTab("personal")}
            icon={Sparkles}
            label="Personal"
            count={personalGroups.length}
            testId="collection-tab-personal"
            accent="text-rose-400"
          />
        </div>
      )}

      {grouped.length === 0 ? (
        <div className="text-center py-20">
          <Archive className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-lg">No models yet</p>
          <p className="text-zinc-600 text-sm mt-1">Start collecting from the catalog</p>
          <Button onClick={() => navigate("/catalog")} className="mt-4 bg-rose-600 hover:bg-rose-500 text-white">Browse Catalog</Button>
        </div>
      ) : visibleGroups.length === 0 ? (
        <div data-testid="collection-tab-empty" className="text-center py-20">
          {sourceTab === "personal" ? (
            <>
              <Sparkles className="w-12 h-12 text-rose-500/40 mx-auto mb-3" />
              <p className="text-zinc-300 text-lg">No personal items yet</p>
              <p className="text-zinc-500 text-sm mt-1 max-w-sm mx-auto leading-relaxed">
                Use Scan Photo or Scan Barcode in the catalog — when an item isn't in the official catalog, you can save it here as a custom item with the photo + AI fields pre-filled.
              </p>
              <Button onClick={() => navigate("/catalog")} className="mt-4 bg-rose-600 hover:bg-rose-500 text-white">
                <Camera className="w-4 h-4 mr-1.5" /> Scan something new
              </Button>
            </>
          ) : (
            <>
              <Archive className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-lg">No catalog releases yet in your collection</p>
              <Button onClick={() => navigate("/catalog")} className="mt-4 bg-rose-600 hover:bg-rose-500 text-white">Browse Catalog</Button>
            </>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div data-testid="collection-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {visibleGroups.map(({ release, entries }) => (
            <CollectionGridCard
              key={release.release_id || entries[0]?.release_id}
              release={release}
              entries={entries}
              onNavigate={(id) => navigate(`/catalog/${id}`)}
            />
          ))}
        </div>
      ) : (
        <div data-testid="collection-list" className="space-y-3">
          {visibleGroups.map(({ release, entries }) => (
            <CollectionGroupCard
              key={release.release_id || entries[0]?.release_id}
              release={release}
              entries={entries}
              isExpanded={expandedReleases[release.release_id || entries[0]?.release_id]}
              onToggle={toggleExpand}
              onEdit={startEdit}
              onRemove={handleRemove}
              onNavigate={(id) => navigate(`/catalog/${id}`)}
            />
          ))}
        </div>
      )}

      <Dialog open={!!editEntry} onOpenChange={() => setEditEntry(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Edit Purchase Record</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <PurchaseFormFields form={editForm} setForm={setEditForm} />
            <Button onClick={handleUpdate} className="w-full bg-rose-600 hover:bg-rose-500 text-white">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
